/* eslint-disable max-len */
import { Field, Form, Formik } from 'formik';
import React, { useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import { DropdownComponent } from '../../../components/prime-formik';
import { compare, getFormattedSubscriptionCost, getNumberOfEmployeesLabel } from '../../../utils/AppUtil';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';
import {
  AccountType,
  NumberOfEmployees,
  PaymentMethod,
  STRIPE_INVOICE_FOR_BANK_TRANSFER_CREATED,
} from '../../../shared/Constants';
import FirstSubscriptionWithFreeTrialDialog from '../../../components/modal/FirstSubscriptionWithFreeTrialDialog';
import { BankTransferTermsOfUseCheckbox } from '../../../components/AgreementCheckboxes';
import useSubscription from '../../../hooks/useSubscription';
import { useHistory } from 'react-router-dom';
import SendPaymentLinkForm from '../../buy-credits/components/SendPaymentLinkForm';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_PAYMENT_LINK_DIALOG_VISIBILITY = 'SET_PAYMENT_LINK_DIALOG_VISIBILITY';
const SET_COMPUTED_SUBSCRIPTION_COST = 'SET_COMPUTED_SUBSCRIPTION_COST';
const SET_FIRST_SUBSCRIPTION_DIALOG_VISIBILITY = 'SET_FIRST_SUBSCRIPTION_DIALOG_VISIBILITY';

const CONFIG_QUERY = gql`
  query configQuery($accountId: ID!) {
    activeLocales
    availableNumbersOfEmployees(id: $accountId)
    availablePaymentMethods
    availableSubscriptionPeriods {
      id
      duration
    }
    availableCountryIndices {
      id
      countryCode
      indexValue
    }
  }
`;

const BUY_SUBSCRIPTION_MUTATION = gql`
  mutation buySubscriptionMutation($input: BuySubscriptionInput!) {
    buySubscription(input: $input) {
      url
    }
  }
`;

const SEND_SUBSCRIPTION_LINK_MUTATION = gql`
  mutation sendSubscriptionLinkMutation($input: SendSubscriptionLinkInput!) {
    sendSubscriptionLink(input: $input) {
      email
      amountOfCredits
    }
  }
`;

const COMPUTE_SUBSCRIPTION_COST_MUTATION = gql`
  mutation computeSubscriptionCostMutation($input: ComputeSubscriptionCostInput!) {
    computeSubscriptionCost(input: $input) {
      computedCost
      computedCostBeforeGscDiscountForClient
      discountPercentage
    }
  }
`;

const i18nMessages = defineMessages({
  buyNoLimit: {
    id: 'account.subscription.buy-no-limit',
    defaultMessage: 'Buy NoLimit',
  },
  createYourNoLimitSubscription: {
    id: 'account.subscription.buy-your-no-limit-subscription',
    defaultMessage: 'Create your NoLimit subscription',
  },
  numberOfCandidatesIncluded: {
    id: 'account.subscription.number-of-candidates-included',
    defaultMessage: 'Number of candidates included:',
  },
  unlimited: {
    id: 'account.subscription.unlimited',
    defaultMessage: 'Unlimited',
  },
  numberOfEmployeesIncluded: {
    id: 'account.subscription.number-of-employees-included',
    defaultMessage: 'Number of employees included:',
  },
  prePaidSubscriptionPeriod: {
    id: 'account.subscription.pre-paid-subscription-period',
    defaultMessage: 'Pre-paid subscription period:',
  },
  attachSubscriptionTo: {
    id: 'account.subscription.attach-subscription-to',
    defaultMessage: 'Attach this subscription to:',
  },
  priceAndPayment: {
    id: 'account.subscription.price-and-payment',
    defaultMessage: 'Price and payment:',
  },
  discount: {
    id: 'account.subscription.discount',
    defaultMessage: 'Discount:',
  },
  priceForSubscriptionPeriod: {
    id: 'account.subscription.price-for-subscription-period',
    defaultMessage: 'Price for subscription period:',
  },
  clientPriceForSubscriptionPeriod: {
    id: 'account.subscription.client-price-for-subscription-period',
    defaultMessage: 'Client price for subscription period:',
  },
  gscPriceForSubscriptionPeriod: {
    id: 'account.subscription.gsc-price-for-subscription-period',
    defaultMessage: 'GSC price for subscription period:',
  },
  availablePaymentMethods: {
    id: 'account.subscription.available-payment-methods',
    defaultMessage: 'Available payment methods:',
  },
  payNow: {
    id: 'account.generic.pay-now',
    defaultMessage: 'Pay now',
  },
  buyNow: {
    id: 'account.generic.buy-now',
    defaultMessage: 'Buy now',
  },
  emailPaymentLink: {
    id: 'account.subscription.email-payment-link',
    defaultMessage: 'E-mail payment link',
  },
  emailPaymentLinkTooltipFirstParagraph: {
    id: 'account.subscription.email-payment-link-tooltip.first-paragraph',
    defaultMessage:
      'If you cannot pay right now, you can send a payment link to yourself and pay later - or if someone else in your organisation should pay, you can send the payment link to this person.',
  },
  emailPaymentLinkTooltipSecondParagraph: {
    id: 'account.subscription.email-payment-link-tooltip.second-paragraph',
    defaultMessage: 'Your account is activated when the payment is completed.',
  },
  bankTransferWithTrialPeriod: {
    id: 'account.subscription.bank-transfer-with-trial-period',
    defaultMessage:
      'Your account is activated for the free trial period and the invoice covering the first payable subscription period is sent to your e-mail address. If your payment is not received within 90 days, the invoice is credited and your account is deleted according to the Terms of Service.',
  },
  bankTransferWithoutTrialPeriod: {
    id: 'account.subscription.bank-transfer-without-trial-period',
    defaultMessage:
      'The invoice is sent to your e-mail address and your account is activated when your payment is received. If not received within 90 days, the invoice is credited and your account is deleted according to the Terms of Service.',
  },
});

const BuySubscription = ({ managingAccount, clientAccount }) => {
  const { id: managingAccountId, type: managingAccountType } = managingAccount;

  const {
    id: clientAccountId,
    type: clientAccountType,
    subscriptionInformation: { hasNoPreviousSubscription, hasTrialPeriod, freeTrialMonths },
  } = clientAccount;

  const gscIsBuyingSubscriptionForClient =
    managingAccountType === AccountType.GSC && clientAccountType === AccountType.NBM_CLIENT;
  const clientAccountIsGscOrGp = clientAccountType === AccountType.GSC || clientAccountType === AccountType.GP;

  const {
    isBankTransferPossible,
    getPaymentMethodOptions,
    getNumberOfEmployeesOptions,
    getSubscriptionPeriodOptions,
    buildAccountLabel,
  } = useSubscription();

  const history = useHistory();
  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  const {
    cldr: { localeDisplayName },
  } = useSelector((state) => state.i18n);

  const [buySubscriptionMutation] = useMutation(BUY_SUBSCRIPTION_MUTATION);
  const [sendSubscriptionLinkMutation] = useMutation(SEND_SUBSCRIPTION_LINK_MUTATION);
  const [computeSubscriptionCostMutation] = useMutation(COMPUTE_SUBSCRIPTION_COST_MUTATION);

  const initialState = {
    firstSubscriptionDialogVisible: false,
    sendPaymentLinkDialogVisible: false,
    supportedLanguages: [],
    numbersOfEmployees: [],
    paymentMethods: [],
    subscriptionPeriods: [],
    countryIndices: [],
    subscriptionCost: 0,
    subscriptionCostBeforeGscDiscount: 0,
    discountPercentage: 0,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          ...state,
          supportedLanguages: action.payload.supportedLanguages,
          numbersOfEmployees: action.payload.numbersOfEmployees,
          paymentMethods: action.payload.paymentMethods,
          subscriptionPeriods: action.payload.subscriptionPeriods,
          countryIndices: action.payload.countryIndices,
        };
      case SET_PAYMENT_LINK_DIALOG_VISIBILITY:
        return {
          ...state,
          sendPaymentLinkDialogVisible: action.payload.sendPaymentLinkDialogVisible,
        };
      case SET_FIRST_SUBSCRIPTION_DIALOG_VISIBILITY:
        return {
          ...state,
          firstSubscriptionDialogVisible: action.payload.firstSubscriptionDialogVisible,
        };
      case SET_COMPUTED_SUBSCRIPTION_COST:
        return {
          ...state,
          subscriptionCost: action.payload.subscriptionCost,
          subscriptionCostBeforeGscDiscount: action.payload.subscriptionCostBeforeGscDiscount,
          discountPercentage: action.payload.discountPercentage,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    firstSubscriptionDialogVisible,
    sendPaymentLinkDialogVisible,
    supportedLanguages,
    numbersOfEmployees,
    subscriptionPeriods,
    paymentMethods,
    subscriptionCost,
    subscriptionCostBeforeGscDiscount,
    discountPercentage,
  } = state;

  const { loading, error, data } = useQuery(CONFIG_QUERY, {
    variables: {
      accountId: clientAccountId,
    },
  });

  const computeSubscriptionCost = async (numberOfEmployees, subscriptionPeriodId) => {
    try {
      const {
        data: {
          computeSubscriptionCost: { computedCost, computedCostBeforeGscDiscountForClient, discountPercentage },
        },
      } = await computeSubscriptionCostMutation({
        variables: {
          input: {
            managingAccountId,
            clientAccountId,
            numberOfEmployees,
            subscriptionPeriodId,
          },
        },
      });

      dispatch({
        type: SET_COMPUTED_SUBSCRIPTION_COST,
        payload: {
          subscriptionCost: computedCost,
          subscriptionCostBeforeGscDiscount: computedCostBeforeGscDiscountForClient,
          discountPercentage,
        },
      });
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const {
    activeLocales,
    availableNumbersOfEmployees,
    availablePaymentMethods,
    availableSubscriptionPeriods,
    availableCountryIndices,
  } = data;

  useEffect(() => {
    const dataIsAvailable =
      activeLocales &&
      activeLocales.length > 0 &&
      availableNumbersOfEmployees &&
      availableNumbersOfEmployees.length > 0 &&
      availablePaymentMethods &&
      availablePaymentMethods.length > 0 &&
      availableCountryIndices &&
      availableSubscriptionPeriods.length > 0 &&
      availableSubscriptionPeriods &&
      availableCountryIndices.length > 0;

    if (dataIsAvailable) {
      const availableSupportedLanguages = activeLocales.map((lang) => ({
        value: lang,
        label: localeDisplayName(lang),
      }));

      const sortedSupportedLanguages = availableSupportedLanguages.sort(compare);

      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          supportedLanguages: sortedSupportedLanguages,
          numbersOfEmployees: availableNumbersOfEmployees,
          paymentMethods: availablePaymentMethods,
          subscriptionPeriods: availableSubscriptionPeriods,
          countryIndices: availableCountryIndices,
        },
      };

      dispatch(action);

      computeSubscriptionCost(availableNumbersOfEmployees[0], availableSubscriptionPeriods[0].id);
    }
    // eslint-disable-next-line
  }, [
    activeLocales,
    availableCountryIndices,
    availableNumbersOfEmployees,
    availablePaymentMethods,
    availableSubscriptionPeriods,
    error,
    localeDisplayName,
  ]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const initialValues = {
    numberOfEmployees: numbersOfEmployees[0],
    subscriptionPeriodId: subscriptionPeriods.length > 0 ? subscriptionPeriods[0].id : '',
    paymentMethod: paymentMethods[0],
    bankTransferTermsOfUse: false,
  };

  const validationSchema = Yup.object().shape({
    numberOfEmployees: Yup.string().required(),
    subscriptionPeriodId: Yup.string().required(),
    paymentMethod: Yup.string().required(),
    bankTransferTermsOfUse: Yup.boolean()
      .when(['numberOfEmployees', 'subscriptionPeriodId', 'paymentMethod'], {
        is: (numberOfEmployees, subscriptionPeriodId, paymentMethod) => {
          return isBankTransferPossible(
            managingAccountType,
            clientAccountType,
            numberOfEmployees,
            subscriptionPeriodId,
            subscriptionPeriods,
            paymentMethod,
          );
        },
        then: Yup.boolean()
          .required()
          .oneOf([true]),
      })
      .notRequired(),
  });

  const buySubscription = async (values, resetForm) => {
    try {
      const {
        data: {
          buySubscription: { url },
        },
      } = await buySubscriptionMutation({
        variables: {
          input: {
            numberOfEmployees: values.numberOfEmployees,
            subscriptionPeriodId: values.subscriptionPeriodId,
            paymentMethod: values.paymentMethod,
            managingAccountId,
            clientAccountId,
            sum: subscriptionCost,
          },
        },
      });

      if (url === STRIPE_INVOICE_FOR_BANK_TRANSFER_CREATED) {
        history.push(`/accounts/${clientAccountId}`);
      } else {
        window.open(url, '_blank', 'noreferrer');
      }
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    } finally {
      resetForm();
      computeSubscriptionCost(numbersOfEmployees[0], subscriptionPeriods[0].id);
    }
  };

  const showFirstSubscriptionWithFreeTrialDialog = hasNoPreviousSubscription && hasTrialPeriod && freeTrialMonths;

  const tooltipText = `${formatMessage(i18nMessages.emailPaymentLinkTooltipFirstParagraph)}

    ${formatMessage(i18nMessages.emailPaymentLinkTooltipSecondParagraph)}
  `;

  const regularSubscriptionCostSection = (
    <div className="flex-box">
      <div>{formatMessage(i18nMessages.priceForSubscriptionPeriod)}</div>
      <div>{`${subscriptionCost.toLocaleString()} $`}</div>
    </div>
  );

  const gscForClientSubscriptionCostSection = (
    <>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.clientPriceForSubscriptionPeriod)}</div>
        <div>{getFormattedSubscriptionCost(subscriptionCostBeforeGscDiscount)}</div>
      </div>
      <div className="flex-box">
        <div>{formatMessage(i18nMessages.gscPriceForSubscriptionPeriod)}</div>
        <div>{getFormattedSubscriptionCost(subscriptionCost)}</div>
      </div>
    </>
  );

  const creditCostSection = gscIsBuyingSubscriptionForClient
    ? gscForClientSubscriptionCostSection
    : regularSubscriptionCostSection;

  const cannotSelectNumberOfEmployees =
    clientAccountIsGscOrGp ||
    (availableNumbersOfEmployees.length === 1 &&
      availableNumbersOfEmployees[0] !== NumberOfEmployees.SELECTED_BY_CLIENT);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        buySubscription(values, resetForm);
      }}
    >
      {({
        isSubmitting,
        setFieldValue,
        handleSubmit,
        isValid,
        values: { numberOfEmployees, subscriptionPeriodId, paymentMethod },
      }) => {
        const paymentMethodOptions = getPaymentMethodOptions(
          managingAccountType,
          clientAccountType,
          numberOfEmployees,
          subscriptionPeriodId,
          subscriptionPeriods,
          paymentMethods,
        );

        return (
          <>
            <FirstSubscriptionWithFreeTrialDialog
              visible={firstSubscriptionDialogVisible}
              freeTrialMonths={freeTrialMonths}
              handleContinue={() => {
                handleSubmit();
                dispatch({
                  type: SET_FIRST_SUBSCRIPTION_DIALOG_VISIBILITY,
                  payload: {
                    firstSubscriptionDialogVisible: false,
                  },
                });
              }}
            />
            <SendPaymentLinkForm
              sendPaymentLinkDialogVisible={sendPaymentLinkDialogVisible}
              hideForm={() => {
                dispatch({
                  type: SET_PAYMENT_LINK_DIALOG_VISIBILITY,
                  payload: {
                    sendPaymentLinkDialogVisible: false,
                  },
                });
              }}
              additionalInput={{
                numberOfEmployees,
                subscriptionPeriodId,
                paymentMethod,
                sum: subscriptionCost,
                hasFreeTrial: hasTrialPeriod,
                managingAccountId,
                clientAccountId,
              }}
              supportedLanguages={supportedLanguages}
              sendCreditLinkMutation={sendSubscriptionLinkMutation}
            />
            <div className="buy-subscription-container">
              <Panel header={formatMessage(i18nMessages.buyNoLimit)}>
                <Form onSubmit={handleSubmit}>
                  <section className="form-section">
                    <p>
                      <span className="underlined">{formatMessage(i18nMessages.createYourNoLimitSubscription)}</span>
                    </p>
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.numberOfCandidatesIncluded)}</div>
                      <div>{formatMessage(i18nMessages.unlimited)}</div>
                    </div>
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.numberOfEmployeesIncluded)}</div>
                      {cannotSelectNumberOfEmployees ? (
                        <div>{getNumberOfEmployeesLabel(numberOfEmployees)}</div>
                      ) : (
                        <Field
                          id="numberOfEmployees"
                          name="numberOfEmployees"
                          component={DropdownComponent}
                          options={getNumberOfEmployeesOptions(numbersOfEmployees, clientAccountIsGscOrGp)}
                          filter
                          filterBy="label,value"
                          onChange={(event) => {
                            setFieldValue('numberOfEmployees', event.value);
                            computeSubscriptionCost(event.value, subscriptionPeriodId);
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.prePaidSubscriptionPeriod)}</div>
                      <Field
                        id="subscriptionPeriodId"
                        name="subscriptionPeriodId"
                        component={DropdownComponent}
                        options={getSubscriptionPeriodOptions(subscriptionPeriods)}
                        filter
                        filterBy="label,value"
                        onChange={(event) => {
                          setFieldValue('subscriptionPeriodId', event.value);
                          computeSubscriptionCost(numberOfEmployees, event.value);
                        }}
                      />
                    </div>
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.attachSubscriptionTo)}</div>
                      <div>{buildAccountLabel(clientAccount)}</div>
                    </div>
                  </section>
                  <section className="form-section">
                    <p>
                      <span className="underlined">{formatMessage(i18nMessages.priceAndPayment)}</span>
                    </p>
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.discount)}</div>
                      <div>{`${discountPercentage}%`}</div>
                    </div>
                    {creditCostSection}
                    <div className="flex-box">
                      <div>{formatMessage(i18nMessages.availablePaymentMethods)}</div>
                      <Field
                        id="paymentMethod"
                        name="paymentMethod"
                        component={DropdownComponent}
                        options={paymentMethodOptions}
                        filter
                        filterBy="label,value"
                      />
                    </div>
                  </section>
                  {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                    <>
                      <div>
                        {hasTrialPeriod && freeTrialMonths
                          ? formatMessage(i18nMessages.bankTransferWithTrialPeriod)
                          : formatMessage(i18nMessages.bankTransferWithoutTrialPeriod)}
                      </div>
                      <br />
                      <div>
                        <Field
                          id="bankTransferTermsOfUse"
                          name="bankTransferTermsOfUse"
                          component={BankTransferTermsOfUseCheckbox}
                        />
                      </div>
                      <br />
                      <div className="align-center">
                        <Button
                          type="submit"
                          disabled={isSubmitting || !isValid}
                          label={formatMessage(i18nMessages.buyNow)}
                          className="button"
                          onClick={handleSubmit}
                        />
                      </div>
                    </>
                  )}
                  {paymentMethod === PaymentMethod.CREDIT_CARD && (
                    <>
                      <section className="buttons">
                        <Button
                          type={showFirstSubscriptionWithFreeTrialDialog ? 'button' : 'submit'}
                          disabled={showFirstSubscriptionWithFreeTrialDialog ? false : isSubmitting}
                          label={formatMessage(i18nMessages.payNow)}
                          className="button"
                          onClick={() => {
                            if (showFirstSubscriptionWithFreeTrialDialog) {
                              dispatch({
                                type: SET_FIRST_SUBSCRIPTION_DIALOG_VISIBILITY,
                                payload: {
                                  firstSubscriptionDialogVisible: true,
                                },
                              });
                            } else {
                              handleSubmit();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          label={formatMessage(i18nMessages.emailPaymentLink)}
                          tooltip={tooltipText}
                          className="button"
                          onClick={() =>
                            dispatch({
                              type: SET_PAYMENT_LINK_DIALOG_VISIBILITY,
                              payload: {
                                sendPaymentLinkDialogVisible: true,
                              },
                            })
                          }
                          icon="fas fa-question-circle"
                          iconPos="right"
                        />
                      </section>
                    </>
                  )}
                </Form>
              </Panel>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

BuySubscription.propTypes = {
  managingAccount: PropTypes.string.isRequired,
  clientAccount: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default BuySubscription;
